import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Clock, User, BookmarkPlus, BookmarkCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useBlogPosts } from '@/hooks/useBlogPosts';
import { ReadingProgressBar } from '@/components/blog/ReadingProgressBar';
import { RelatedArticles } from '@/components/blog/RelatedArticles';
import { BlogSidebar } from '@/components/blog/BlogSidebar';
import { Logo } from '@/components/ui/Logo';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { BookmarkButton } from '@/components/blog/BookmarkButton';
import { SocialShareButtons } from '@/components/blog/SocialShareButtons';
import { DownloadCTA } from '@/components/blog/DownloadCTA';
import { useTrackArticleView } from '@/hooks/useArticleViews';
import { useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import DOMPurify from 'dompurify';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: blogPosts = [], isLoading } = useBlogPosts();
  const trackView = useTrackArticleView();

  // Find post by slug (convert title to slug format)
  const post = blogPosts.find(p => 
    p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') === slug
  );

  // Sanitize content before rendering to prevent XSS
  const sanitizedContent = useMemo(() => {
    if (!post?.content) return '';
    return DOMPurify.sanitize(post.content, {
      ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'strong', 'em', 'br', 'a', 'blockquote', 'span', 'div', 'img'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class', 'src', 'alt'],
      ALLOW_DATA_ATTR: false
    });
  }, [post?.content]);

  useEffect(() => {
    if (post) {
      trackView.mutate(post.id);
    }
  }, [post?.id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center text-muted-foreground">Loading article…</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Article not found</h1>
          <Button onClick={() => navigate('/blog')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }


  const articleUrl = `https://odyvoy.com/blog/${slug}`;
  const publishDate = new Date(post.date).toISOString();

  // Keep total <title> under 60 chars: " | OdyVoy" suffix is 9 chars, so cap post title at 50.
  const seoTitle =
    post.title.length > 50 ? `${post.title.slice(0, 47).trimEnd()}…` : post.title;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{`${seoTitle} | OdyVoy`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={articleUrl} />
        <meta name="keywords" content={post.tags.join(', ')} />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:image" content={post.image.startsWith('http') ? post.image : `https://odyvoy.com${post.image}`} />
        <meta property="article:published_time" content={publishDate} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        {post.tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.image.startsWith('http') ? post.image : `https://odyvoy.com${post.image}`} />
        
        {/* Structured Data - Article */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.image.startsWith('http') ? post.image : `https://odyvoy.com${post.image}`,
            "datePublished": publishDate,
            "dateModified": publishDate,
            "author": {
              "@type": "Person",
              "name": post.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "OdyVoy",
              "logo": {
                "@type": "ImageObject",
                "url": "https://odyvoy.com/lovable-uploads/odyvoy-logo.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": articleUrl
            },
            "keywords": post.tags.join(', '),
            "articleSection": post.category,
            "wordCount": post.content.split(/\s+/).length
          })}
        </script>
        
        {/* Structured Data - Breadcrumb */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://odyvoy.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://odyvoy.com/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": post.title,
                "item": articleUrl
              }
            ]
          })}
        </script>
      </Helmet>

      <ReadingProgressBar />

      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 sm:h-16 items-center justify-between gap-2 px-3 sm:px-4">
          <button onClick={() => navigate(user ? '/dashboard' : '/')} className="shrink-0" aria-label="Go to homepage">
            <Logo className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16" />
          </button>
          <div className="flex items-center gap-1 sm:gap-3">
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={() => navigate('/blog')} className="h-9 px-2 sm:px-3" aria-label="Back to blog">
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Back to Blog</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content */}
          <article className="lg:col-span-8">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <Link to={user ? '/dashboard' : '/'} className="hover:text-foreground">
                {user ? 'Dashboard' : 'Home'}
              </Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-foreground">Blog</Link>
              <span>/</span>
              <span className="text-foreground">{post.category}</span>
            </nav>

            {/* Category Badge */}
            <Badge variant="secondary" className="mb-4">
              {post.category}
            </Badge>

            {/* Title */}
            <h1 className="text-4xl font-bold mb-4 leading-tight">
              {post.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <time dateTime={publishDate}>
                    {new Date(post.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </time>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{post.readTime}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <BookmarkButton post={post} />
                <SocialShareButtons 
                  title={post.title}
                  excerpt={post.excerpt}
                  url={articleUrl}
                  image={post.image}
                />
              </div>
            </div>

            {/* Featured Image */}
            {post.image && (
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-[400px] object-cover rounded-lg mb-8"
              />
            )}

            {/* Content */}
            <div 
              className="prose prose-lg dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />

            {/* Tags and Share */}
            {post.tags.length > 0 && (
              <div className="mt-8 pt-8 border-t">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold mb-3">Tags:</h3>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                          onClick={() => navigate('/blog', { state: { searchTag: tag } })}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <p className="text-sm font-semibold">Share this article:</p>
                    <SocialShareButtons 
                      title={post.title}
                      excerpt={post.excerpt}
                      url={articleUrl}
                      image={post.image}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Download CTA */}
            <DownloadCTA articleId={post.id} articleTitle={post.title} />

            {/* Related Articles */}
            <div className="mt-12">
              <RelatedArticles
                currentPost={post}
                allPosts={blogPosts}
                onPostClick={(relatedPost) => {
                  const relatedSlug = relatedPost.title
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, '');
                  navigate(`/blog/${relatedSlug}`);
                  window.scrollTo(0, 0);
                }}
              />
            </div>
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-24">
              <BlogSidebar 
                posts={blogPosts}
                selectedCategory={post.category}
                onCategorySelect={(category) => navigate('/blog', { state: { category } })}
                onTagClick={(tag) => navigate('/blog', { state: { searchTag: tag } })}
                onArticleClick={(clickedPost) => {
                  const clickedSlug = clickedPost.title
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, '');
                  navigate(`/blog/${clickedSlug}`);
                  window.scrollTo(0, 0);
                }}
              />
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default BlogPost;
